import React, { useState } from "react";
import { FiLayers, FiRotateCcw, FiPlus, FiUploadCloud } from "react-icons/fi";
import { HiSparkles } from "react-icons/hi2";
import { Tooltip, Select, Input, Button, Badge } from "antd";
import PageName from "../../components/react_helmet/PageName";
import ProductGroupsTable from "../../components/table/ProductGroupsTable";
import GeneralButton from "../../components/button/GeneralButton";
import useProductGroups from "./customHooks/useProductGroups";
import CreateProductGroupModal from "../../components/modals/CreateProductGroupModal";
import BulkMigrateModal from "../../components/modals/BulkMigrateModal";
import BulkUploadGroupsModal from "../../components/modals/BulkUploadGroupsModal";
import { BulkHumanizeGroupsModal } from "../../components/modals/BulkHumanizeModal";
import { deleteProductGroup } from "../../api/products.api";
import toast from "react-hot-toast";

const ProductGroups = () => {
    const {
        productGroups,
        rawGroups,
        pagination,
        loading,
        handleTableChange,
        mfgId, setMfgId,
        categoryId, setCategoryId,
        searchTerm, setSearchTerm,
        groupType, setGroupType,
        resetFilters,
        refreshData: triggerRefetch,
        isFiltered,
        manufacturersList,
        categoriesList
    } = useProductGroups();

    const [modalOpen, setModalOpen] = useState(false);
    const [bulkMigrateOpen, setBulkMigrateOpen] = useState(false);
    const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
    const [bulkHumanizeOpen, setBulkHumanizeOpen] = useState(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);
    const [editData, setEditData] = useState(null);

    const toggleModal = () => setModalOpen(!modalOpen);
    const toggleBulkMigrate = () => setBulkMigrateOpen(!bulkMigrateOpen);
    const toggleBulkUpload = () => setBulkUploadOpen(!bulkUploadOpen);
    const toggleBulkHumanize = () => setBulkHumanizeOpen(!bulkHumanizeOpen);

    const rowSelection = {
        selectedRowKeys,
        onChange: (keys, rows) => {
            setSelectedRowKeys(keys);
            setSelectedRows(rows);
        },
    };

    const refreshData = () => {
        setSelectedRowKeys([]);
        setSelectedRows([]);
        handleTableChange({ current: 1, pageSize: pagination.pageSize }, {}, {}); 
        if (triggerRefetch) triggerRefetch();
    };

    const handleDeleteGroup = (groupId) => {
        deleteProductGroup(groupId, (res) => {
            if (res.status === 200 || res?.success) {
                toast.success("Product Group deleted successfully!");
                refreshData();
            } else {
                toast.error(res.message || "Failed to delete product group.");
            }
        });
    };

    const mfgOptions = React.useMemo(() => {
        const map = new Map();
        if (Array.isArray(manufacturersList)) {
            manufacturersList.forEach((m) => {
                if (m && (m._id || m.id)) {
                    const id = String(m._id || m.id);
                    map.set(id, m.name || m.mfg_name || id);
                }
            });
        }
        if (Array.isArray(rawGroups)) {
            rawGroups.forEach((g) => {
                const id = g?.mfg_id || g?.mfgId || g?.mfg?._id || g?.manufacturer?._id;
                const name = g?.mfg_name || g?.mfg?.name || g?.manufacturer?.name;
                if (id && name && !map.has(String(id))) {
                    map.set(String(id), name);
                }
            });
        }
        return Array.from(map.entries()).map(([value, label]) => ({ value, label }));
    }, [manufacturersList, rawGroups]);

    const categoryOptions = React.useMemo(() => {
        const map = new Map();
        if (Array.isArray(categoriesList)) {
            categoriesList.forEach((c) => {
                if (c && (c._id || c.id)) {
                    const id = String(c._id || c.id);
                    map.set(id, c.categoryName || c.name || id);
                }
            });
        }
        if (Array.isArray(rawGroups)) {
            rawGroups.forEach((g) => {
                const id = g?.categoryId || g?.category_id || g?.category?._id;
                const name = g?.categoryName || g?.category?.categoryName || g?.category?.name;
                if (id && name && !map.has(String(id))) {
                    map.set(String(id), name);
                }
            });
        }
        return Array.from(map.entries()).map(([value, label]) => ({ value, label }));
    }, [categoriesList, rawGroups]);

    return (
        <div className="settings-container">
            <PageName title={"Product Variant/Groups"} />
            <CreateProductGroupModal isOpen={modalOpen} toggle={toggleModal} refreshData={refreshData} editData={editData} setEditData={setEditData} />
            <BulkMigrateModal isOpen={bulkMigrateOpen} toggle={toggleBulkMigrate} refreshData={refreshData} />
            <BulkUploadGroupsModal isOpen={bulkUploadOpen} toggle={toggleBulkUpload} refreshData={refreshData} />
            <BulkHumanizeGroupsModal 
                isOpen={bulkHumanizeOpen} 
                toggle={toggleBulkHumanize} 
                initialItems={selectedRows} 
                refreshData={refreshData} 
            />

            <div className="px-4">
                {/* Top Section: Header Title & Main Action Buttons */}
                <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-3">
                    <div className="d-flex align-items-center gap-3">
                        <div className="bg-primary-soft p-3 rounded-circle shadow-sm">
                            <FiLayers size={24} className="text-primary" />
                        </div>
                        <div>
                            <h4 className="fw-bold text-dark mb-0">Product Variant/Groups</h4>
                            <p className="text-muted small mb-0 mt-1">Manage grouped products, attributes, and variant relations</p>
                        </div>
                    </div>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                        <Button 
                            className="d-flex align-items-center gap-2 px-3 py-2 bg-white text-primary border-primary shadow-sm"
                            style={{ height: '40px', borderRadius: '8px', fontWeight: 600 }}
                            onClick={toggleBulkHumanize}
                        >
                            <HiSparkles size={16} className="text-warning" /> 
                            Bulk Humanize
                            {selectedRowKeys.length > 0 && (
                                <span className="badge bg-primary text-white rounded-pill px-2" style={{ fontSize: '11px' }}>
                                    {selectedRowKeys.length}
                                </span>
                            )}
                        </Button>
                        <Button 
                            className="d-flex align-items-center gap-2 px-3 py-2 bg-white text-primary border-primary shadow-sm"
                            style={{ height: '40px', borderRadius: '8px', fontWeight: 600 }}
                            onClick={toggleBulkUpload}
                        >
                            <FiUploadCloud size={16} /> Bulk Upload from File
                        </Button>
                        <Button 
                            className="d-flex align-items-center gap-2 px-3 py-2 bg-white text-primary border-primary shadow-sm"
                            style={{ height: '40px', borderRadius: '8px', fontWeight: 600 }}
                            onClick={toggleBulkMigrate}
                        >
                            <FiLayers size={16} /> Bulk Migrate
                        </Button>
                        <GeneralButton 
                            title={"Create Group"} 
                            className={"create-user-btn text-white shadow-sm px-4 d-flex align-items-center gap-2"} 
                            icon={<FiPlus size={18} />}
                            handleClick={toggleModal} 
                        />
                    </div>
                </div>

                {/* Filter Toolbar Section */}
                <div className="search-input-wrapper d-flex justify-content-between align-items-center mb-4 p-3 bg-white rounded-3 shadow-sm flex-wrap gap-3">
                    <div className="d-flex align-items-center gap-2 flex-wrap flex-grow-1">
                        <Input.Search
                            placeholder="Search by group name or slug..."
                            allowClear
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onSearch={(val) => setSearchTerm(val)}
                            style={{ width: 280 }}
                            size="large"
                        />
                        <Select
                            allowClear
                            placeholder="All Group Types"
                            style={{ width: 160 }}
                            size="large"
                            value={groupType || undefined}
                            onChange={(val) => setGroupType(val || "")}
                            options={[
                                { value: 'variant', label: 'Variant Groups' },
                                { value: 'related', label: 'Related Groups' }
                            ]}
                        />
                        <Select
                            allowClear
                            placeholder="Filter by Manufacturer"
                            style={{ width: 220 }}
                            size="large"
                            value={mfgId || undefined}
                            onChange={(val) => setMfgId(val || "")}
                            options={mfgOptions}
                            showSearch
                            filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                        />
                        <Select
                            allowClear
                            placeholder="Filter by Category"
                            style={{ width: 220 }}
                            size="large"
                            value={categoryId || undefined}
                            onChange={(val) => setCategoryId(val || "")}
                            options={categoryOptions}
                            showSearch
                            filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                        />
                    </div>
                    {isFiltered && (
                        <Button 
                            icon={<FiRotateCcw />} 
                            onClick={resetFilters} 
                            size="large"
                            className="d-flex align-items-center gap-1 text-danger border-danger-subtle"
                        >
                            Reset Filters
                        </Button>
                    )}
                </div>

                {/* Main Data Table */}
                <div className="settings-card shadow-sm border-0 rounded-3">
                    <ProductGroupsTable 
                        data={productGroups} 
                        pagination={pagination} 
                        loading={loading} 
                        rowSelection={rowSelection}
                        onChange={handleTableChange} 
                        onEdit={(record) => { setEditData(record); setModalOpen(true); }} 
                        onDelete={handleDeleteGroup}
                    />
                </div>
            </div>
        </div>
    );
};
export default ProductGroups;